/**
 * engagementRows.ts
 *
 * Helpers for lists that show ONE ROW PER ENGAGEMENT (PRD §4.7): the consultant
 * dashboard cards and the Command Center triage list. A company with several
 * concurrent engagements yields several rows; a company with no engagement yet
 * yields one placeholder row whose `engagementId` is null.
 */

export interface EngagementRowLike {
  companyId: string;
  companyName: string;
  engagementId?: string | null;
  engagementName?: string | null;
}

/** Stable identity for a row: the engagement when there is one, else the company. */
export function engagementRowKey(row: EngagementRowLike): string {
  return row.engagementId || row.companyId;
}

/**
 * Label a row by company, adding the engagement name only when the same company
 * appears more than once in the list (so single-engagement companies stay clean).
 */
export function engagementRowLabel(row: EngagementRowLike, rows: ReadonlyArray<EngagementRowLike>): string {
  const sameCompany = rows.filter((r) => r.companyId === row.companyId).length;
  return sameCompany > 1 && row.engagementName ? `${row.companyName} · ${row.engagementName}` : row.companyName;
}

/** True when the list contains at least one company with two or more rows. */
export function hasMultiEngagementCompany(rows: ReadonlyArray<EngagementRowLike>): boolean {
  const seen = new Set<string>();
  for (const r of rows) {
    if (seen.has(r.companyId)) return true;
    seen.add(r.companyId);
  }
  return false;
}
